<script lang="ts">
  import * as Dialog from '$lib/components/ui/dialog';
  import { accountFieldsMock, tabsMock } from './myaccount.mock';
  import AccountDetailsTab from './Tabs/AccountDetailsTab.svelte';
  import { type User, type AccountFields, type RFQ } from './myaccount.types';
  import MyRfqTab from './Tabs/MyRfqTab.svelte';
  import ChangePasswordTab from './Tabs/ChangePasswordTab.svelte';
  import TabsTrigger from '$lib/components/ui/tabs/tabs-trigger.svelte';
  import Tabs from '$lib/components/ui/tabs/tabs.svelte';
  import TabsList from '$lib/components/ui/tabs/tabs-list.svelte';
  import DeactivateAccountTab from './Tabs/DeactivateAccountTab.svelte';

  let {
    open = $bindable(true),
    user = $bindable<User | undefined>(undefined),
    tabs = $bindable<any>(tabsMock),
    fields = $bindable<AccountFields>(accountFieldsMock),
    title = 'My Account',
    description = '',
    size = 'sm:max-w-[780px]',
    onSave,
    rfqData = $bindable([]),
    experience,
  }: {
    open?: boolean;
    user?: User | undefined;
    fields?: any[];
    title?: string;
    tabs?: any;
    description?: string;
    size?: string;
    onSave?: (u: User) => void;
    rfqData?: RFQ[];
    experience?: any;
  } = $props();
  
  // Authoritative user: DataVault cache, then optional bound prop — never placeholder/mock data
  let userData = $derived.by(() => {
    return experience?.dataVault?.user ?? user;
  });

  let userLoading = $state(false);

  let activeTab = $state<'account' | 'password'>('account');
  let draft = $state({}) as Record<string, unknown>;

  let deactivateAccountOpen = $state(false);

  let modalTitle = $derived(
    deactivateAccountOpen ? 'Delete or deactivate account?' : 'My Account',
  );

  const selectTab = (id: typeof activeTab) => {
    // Prevent access to RFQs tab (disabled)
    if (id === 'rfqs') return;
    activeTab = id;
  };

  // RFQ state - always start empty, only populated from backend (never mock data)
  let rfqs = $state<RFQ[]>([]);
  let rfqLoading = $state(false);
  let rfqError = $state<string | null>(null);
  let rfqLoadedOnce = $state(false);

  // Refetch account from API every time the modal opens
  $effect(() => {
    if (!open) return;
    const exp = experience;
    const fetchUser = exp?.dataVault?.fetchUserData;
    if (typeof fetchUser !== 'function') {
      return;
    }
    let cancelled = false;
    userLoading = true;
    void (async () => {
      try {
        await fetchUser.call(exp.dataVault);
      } finally {
        if (!cancelled) userLoading = false;
      }
    })();
    return () => {
      cancelled = true;
    };
  });

  // Update draft when userData changes (only while modal is open — avoids repopulating after close)
  $effect(() => {
    if (!open) return;
    const currentUser = userData;
    if (currentUser && typeof currentUser === 'object' && Object.keys(currentUser).length > 0) {
      draft = { ...currentUser } as Record<string, unknown>;
    }
  });
  
  // Listen for userData updates from DataVault event (in case it updates after mount)
  $effect(() => {
    if (experience?.eventHub) {
      const updateDraft = (data: User) => {
        // Update draft when userData event is emitted
        draft = { ...data };
      };
      
      experience.eventHub.on('userData', updateDraft);
      
      return () => {
        experience.eventHub.off('userData', updateDraft);
      };
    }
  });

  $effect(() => {
    if (!open) {
      activeTab = 'account';
      deactivateAccountOpen = false;
      // Reset RFQ state when modal closes to ensure fresh data on next open
      rfqs = [];
      rfqData = [];
      rfqLoading = false;
      rfqError = null;
      rfqLoadedOnce = false; // Reset so we fetch fresh data on next open
    }
  });

  // Fetch RFQs when the rfqs tab becomes active - ONLY from backend
  $effect(() => {
    if (activeTab === 'rfqs' && experience && !rfqLoadedOnce) {
      // Clear any existing data before fetching (ensure no mock data)
      rfqs = [];
      rfqData = [];
      rfqLoading = true;
      rfqError = null;
      
      // Emit event to request RFQs from backend via MyAccountManager
      if (experience?.eventHub) {
        experience.eventHub.emit('fetchUserRFQs', {});
      } else {
        rfqLoading = false;
        rfqError = 'Event system not available';
        rfqLoadedOnce = true; // Prevent retry
      }
    }
  });

  // Listen for RFQ data response - ONLY from backend, never mock data
  $effect(() => {
    if (experience?.eventHub) {
      const handleRFQData = (data: RFQ[]) => {
        // Only accept data from backend, ensure it's an array
        const backendData = Array.isArray(data) ? data : [];
        rfqs = backendData;
        rfqData = backendData;
        rfqLoadedOnce = true;
        rfqLoading = false;
        rfqError = null;
        console.log('RFQs loaded from backend:', backendData.length, 'items');
      };

      const handleRFQError = (errorMessage: string) => {
        // On error, ensure we have empty state (no mock data)
        rfqs = [];
        rfqData = [];
        rfqLoadedOnce = true; // Mark as loaded so we don't retry automatically
        rfqLoading = false;
        rfqError = errorMessage || 'Failed to load RFQs';
        console.error('RFQ fetch error:', rfqError);
      };

      experience.eventHub.on('userRFQs:loaded', handleRFQData);
      experience.eventHub.on('userRFQs:error', handleRFQError);

      return () => {
        experience.eventHub.off('userRFQs:loaded', handleRFQData);
        experience.eventHub.off('userRFQs:error', handleRFQError);
      };
    }
  });
</script>

<Dialog.Root bind:open preventScroll={false}>
  <Dialog.Content class={`${size} max-h-[90vh] overflow-y-auto border-none shadow-lg !z-[1000001] !bg-white dark:!bg-[var(--theme-main)] dark:!text-[var(--theme-font)]`}>
    <Dialog.Header class="!p-4 !pb-0 !mt-4">
      <Dialog.Title class="text-xl font-semibold">{modalTitle}</Dialog.Title>
      {#if description}<Dialog.Description>{description}</Dialog.Description
        >{/if}
    </Dialog.Header>

    <div class="flex !flex-col gap-4 !p-4 !px-0 !pt-0">
      {#if !deactivateAccountOpen}
        <div class="flex gap-2 w-full justify-start items-start border-b-0">
          <Tabs
            class="w-full !flex !flex-col !justify-start !items-start !mt-5 md:!mt-0 border-b-[0.1px] border-[#f9f9f9] !px-4"
          >
            <div id="tabs-con" class="px-2 sm:px-3">
              <TabsList class="h-9 pl-4">
                {#each tabs.filter(tab => tab.id !== 'rfqs') as tab}
                  <TabsTrigger
                    onclick={() => selectTab(tab.id)}
                    value={tab.id}
                    class="!text-sm !shadow-none px-3 py-2 border-b-3 border-transparent rounded-none font-light data-[state=active]:font-medium text-[#000] dark:text-[var(--theme-font)] !data-[state=active]:text-[#000] dark:!data-[state=active]:text-[var(--theme-font)] !bg-[#fff] dark:!bg-[var(--theme-main)] hover:!bg-[#f8f8f8] dark:hover:!bg-[var(--theme-canvas)] transition-colors data-[state=active]:border-b-3 
data-[state=active]:border-b-[#004976] dark:data-[state=active]:border-b-[var(--theme-link)] data-[state=active]:rounded-none"
                  >
                    {tab.label}
                  </TabsTrigger>
                {/each}
              </TabsList>
            </div>
          </Tabs>
        </div>

        <div class="flex-1 w-full rounded-md p-4">
          {#if activeTab === 'account'}
            <AccountDetailsTab {fields} bind:draft {experience} accountLoading={userLoading} />
          {:else if activeTab === 'password'}
            <ChangePasswordTab {experience} />
          {:else}
            <div class="flex !flex-col !px-4"></div>
          {/if}
        </div>
      {:else}
        <div class="flex-1 w-full rounded-md p-4">
          <DeactivateAccountTab bind:deactivateAccountOpen user={userData} {experience} />
        </div>
      {/if}
    </div>
  </Dialog.Content>
</Dialog.Root>